import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, ExternalLink, Eye, EyeOff, Check, AlertCircle } from 'lucide-react';
import { SocialMediaPlatform } from '../hooks/useSocialMediaPlatforms';
import oauthService from '../services/oauthService';

interface SocialMediaConnectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  platform: SocialMediaPlatform | null;
  onConnectionSuccess: (platformId: string, credentials: any) => void;
  onConnectionError?: () => void;
}

const SocialMediaConnectionModal: React.FC<SocialMediaConnectionModalProps> = ({
  isOpen,
  onClose,
  platform,
  onConnectionSuccess,
  onConnectionError
}) => {
  const [credentials, setCredentials] = useState({
    accessToken: '',
    refreshToken: '',
    pageId: '',
    businessAccountId: '',
    appId: '',
    appSecret: '',
    clientId: '',
    clientSecret: ''
  });
  const [showSecrets, setShowSecrets] = useState({
    accessToken: false,
    refreshToken: false,
    appSecret: false,
    clientSecret: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Configuración de cada red social
  const platformConfigs = {
    instagram: {
      name: 'Instagram',
      supportsOAuth: true,
      oauthUrl: 'https://api.instagram.com/oauth/authorize',
      requiredFields: ['access_token'],
      optionalFields: ['refresh_token']
    },
    facebook: {
      name: 'Facebook',
      supportsOAuth: true,
      oauthUrl: 'https://www.facebook.com/v18.0/dialog/oauth',
      requiredFields: ['access_token', 'page_id'],
      optionalFields: ['refresh_token']
    },
    tiktok: {
      name: 'TikTok',
      supportsOAuth: true,
      oauthUrl: 'https://www.tiktok.com/v2/auth/authorize',
      requiredFields: ['access_token'],
      optionalFields: ['refresh_token']
    }
  };

  const config = platform ? platformConfigs[platform.id as keyof typeof platformConfigs] : null;

  const handleOAuthConnection = async () => {
    if (!platform || !config || !config.supportsOAuth) {
      setError('Esta red social no soporta autenticación OAuth');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log(`🔗 Iniciando OAuth REAL para ${platform.name}...`);
      
      // Solo Instagram está implementado por ahora
      if (platform.id !== 'instagram') {
        setError(`OAuth no implementado para ${platform.name} aún`);
        setLoading(false);
        return;
      }

      // Iniciar OAuth de Instagram (sin necesidad de App ID/Secret)
      const initResult = await oauthService.initInstagramOAuth();

      if (!initResult.success || !initResult.authUrl) {
        setError(initResult.message || 'Error iniciando OAuth');
        setLoading(false);
        return;
      }

      // Abrir ventana de OAuth
      const oauthWindow = oauthService.openOAuthPopup(initResult.authUrl, platform.id);

      if (!oauthWindow) {
        setError('No se pudo abrir la ventana de autenticación. Verifica que los pop-ups estén habilitados.');
        setLoading(false);
        return;
      }

      // Escuchar el callback OAuth
      const cleanup = oauthService.listenForOAuthCallback(
        platform.id,
        (result) => {
          console.log('✅ OAuth completado exitosamente:', result);
          setLoading(false);
          onConnectionSuccess(platform.id, result.data);
          onClose();
        },
        (error) => {
          console.error('❌ Error en OAuth:', error);
          setError(error || 'Error completando autenticación');
          setLoading(false);
          onConnectionError?.();
        }
      );

      // Verificar si la ventana se cerró prematuramente
      const checkWindow = setInterval(() => {
        if (oauthWindow.closed) {
          clearInterval(checkWindow);
          cleanup();
          if (loading) {
            setError('El proceso de autenticación fue cancelado');
            setLoading(false);
            onConnectionError?.();
          }
        }
      }, 1000);

    } catch (error) {
      console.error('Error en OAuth:', error);
      setError(error instanceof Error ? error.message : 'Error al conectar con la red social');
      setLoading(false);
      onConnectionError?.();
    }
  };

  const handleManualConnection = async () => {
    if (!platform || !config) return;

    setLoading(true);
    setError(null);

    try {
      // Validar campos requeridos
      const requiredFields = config.requiredFields;
      const missingFields = requiredFields.filter(field => {
        const value = credentials[field as keyof typeof credentials];
        return !value || value.trim() === '';
      });

      if (missingFields.length > 0) {
        setError(`Faltan campos requeridos: ${missingFields.join(', ')}`);
        setLoading(false);
        return;
      }

      // Simular validación de credenciales
      console.log(`🔑 Validando credenciales para ${platform.name}...`);
      
      // Simular delay de validación
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Simular validación exitosa
      onConnectionSuccess(platform.id, credentials);
      setLoading(false);
      onClose();

    } catch (error) {
      console.error('Error validando credenciales:', error);
      setError('Error al validar las credenciales. Verifica que sean correctas.');
      setLoading(false);
      onConnectionError?.();
    }
  };

  const toggleSecretVisibility = (field: keyof typeof showSecrets) => {
    setShowSecrets(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const renderOAuthSection = () => (
    <div className="space-y-4">
      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
        <div className="flex items-center space-x-2 mb-2">
          <ExternalLink className="w-5 h-5 text-blue-600" />
          <h3 className="font-medium text-blue-900 dark:text-blue-100">
            Conexión OAuth
          </h3>
        </div>
        <p className="text-sm text-blue-700 dark:text-blue-300 mb-4">
          Se abrirá una ventana para autenticarte con {platform?.name}. 
          Una vez autorizada la conexión, se guardarán automáticamente los tokens de acceso.
        </p>

        <button
          onClick={handleOAuthConnection}
          disabled={loading}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors duration-200 flex items-center justify-center space-x-2"
        >
          {loading ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>Conectando...</span>
            </>
          ) : (
            <>
              <ExternalLink className="w-4 h-4" />
              <span>Conectar con {platform?.name}</span>
            </>
          )}
        </button>
      </div>
    </div>
  );

  const renderManualSection = () => (
    <div className="space-y-4">
      <div className="p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl">
        <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-4">
          Credenciales Manuales
        </h3>
        
        <div className="space-y-4">
          {config?.requiredFields.includes('access_token') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Access Token
              </label>
              <div className="relative">
                <input
                  type={showSecrets.accessToken ? 'text' : 'password'}
                  value={credentials.accessToken}
                  onChange={(e) => setCredentials(prev => ({ ...prev, accessToken: e.target.value }))}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Ingresa tu Access Token"
                />
                <button
                  type="button"
                  onClick={() => toggleSecretVisibility('accessToken')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  {showSecrets.accessToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          )}

          {config?.requiredFields.includes('page_id') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Page ID (Facebook)
              </label>
              <input
                type="text"
                value={credentials.pageId}
                onChange={(e) => setCredentials(prev => ({ ...prev, pageId: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Ingresa tu Page ID"
              />
            </div>
          )}

          {config?.optionalFields.includes('refresh_token') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Refresh Token (Opcional)
              </label>
              <div className="relative">
                <input
                  type={showSecrets.refreshToken ? 'text' : 'password'}
                  value={credentials.refreshToken}
                  onChange={(e) => setCredentials(prev => ({ ...prev, refreshToken: e.target.value }))}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Ingresa tu Refresh Token (opcional)"
                />
                <button
                  type="button"
                  onClick={() => toggleSecretVisibility('refreshToken')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  {showSecrets.refreshToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          )}
        </div>

        <button
          onClick={handleManualConnection}
          disabled={loading}
          className="w-full mt-4 bg-primary-600 text-white py-2 px-4 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors duration-200 flex items-center justify-center space-x-2"
        >
          {loading ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>Validando...</span>
            </>
          ) : (
            <>
              <Check className="w-4 h-4" />
              <span>Conectar Manualmente</span>
            </>
          )}
        </button>
      </div>
    </div>
  );

  if (!platform || !config) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <span className="text-2xl">{platform.icon}</span>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                    Conectar {platform.name}
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Configura la conexión para poder publicar contenido
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-xl text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors duration-200"
              >
                <X size={24} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              {/* Error Message */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="w-4 h-4 text-red-600" />
                    <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
                  </div>
                </div>
              )}

              {/* OAuth Section */}
              {renderOAuthSection()}

              <div className="my-4 text-center text-gray-500">o</div>

              {/* Manual Section */}
              {renderManualSection()}

              {/* Documentation Link */}
              <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl">
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                  ¿Necesitas ayuda para obtener tus credenciales?
                </p>
                <button
                  onClick={() => window.open(`/docs/integraciones/${platform.id}`, '_blank')}
                  className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 underline"
                >
                  Ver documentación de {platform.name}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SocialMediaConnectionModal;

